import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { mapValues } from 'lodash';

import {
  handleForceGasAndElectric,
  handleGasType,
  handleGasValuePosition,
  handleTrackTempControl,
  selectAdmin,
} from '../../store/slices/settings/admin/adminSlice';
import { handleAddElementToBank } from '../../store/slices/settings/admin/addElementToBankAndSystemIdentification';
import { addAdminHeatersService } from '../../../services/ssrs.service';
import {
  bulkUpdateBlowerDeviceService,
  updateBlowerSettingService,
  updateDevicesValveService,
  updateSwitchSettingService,
} from '../../../services';
import { selectedSpecDevices } from '../../../helpers/setting';
import { logger } from '../../../helpers/logger';
import {
  confirmedValves,
  forceGasAndElectricPayload,
  gasTypePayload,
  trackTempControlPayload,
  valveSettingsPayload,
} from '../../../helpers/payloads/settingsPayloads';
import { SETTINGS_HEADERS } from '../settingsHeaders';
import {
  plainMessage,
  saveFailedMessage,
  settingsMessage,
} from './settingsMessage';

/** What the operator has chosen to apply on the admin panel: nothing yet. */
export const noAdminChanges = () => ({
  addElementToBank: false,
  trackTempControl: false,
  deactivateTrackTempControl: false,
  selectGasType: false,
  valveSettings: false,
  systemIdentification: false,
  systemConfiguration: false,
  forceGasAndElectric: false,
});

const TRACKED = 'track temperature control t/c settings have been applied';
const ELEMENT_ADDED = 'add element to bank settings have been applied';
const BOTH_APPLIED =
  'add element to bank & track temperature control t/c settings have been applied';
const FORCED =
  'force - gas & electric system simultaneously on for 15 minutes settings have been applied';
const IDENTIFIED_AND_FORCED =
  'system identification & force - gas & electric system simultaneously on for 15 minutes settings have been applied';

const adminMessage = (system, message, title = 'settings') =>
  settingsMessage(
    'administration settings',
    `${system}-change options`,
    message,
    title,
  );

const machinesOf = (devices) => devices.map(({ machine }) => machine);

/**
 * The admin panel: track temperature control and the heater bank (ESS, TES),
 * gas type and valve positions (TGS), and the system-commands row (system
 * identification through `saveSystemIdentification`, and forcing gas &
 * electric on together). `save(header)` resolves to `{ content, keepOpen }`.
 */
export const useSaveAdmin = ({ saveSystemIdentification }) => {
  const dispatch = useDispatch();
  const admin = useSelector(selectAdmin);

  const [saveChanges, setSaveChanges] = useState(noAdminChanges);
  const [saveInputElement, setSaveInputElement] = useState(null);
  const [gasSelection, setGasSelection] = useState(null);
  const [enableSwitch, setEnableSwitch] = useState(false);
  const [selectSystemConfig, setSelectSystemConfig] = useState(0);

  const tracking =
    saveChanges.trackTempControl || saveChanges.deactivateTrackTempControl;

  // Sends first; Redux changes only once the devices took the setting.
  const applyTrackTempControl = async (swt, send) => {
    const devices = selectedSpecDevices(admin[`${swt}Spec`], 'isSelected');
    if (devices.length > 0) {
      await send(
        machinesOf(devices),
        trackTempControlPayload(saveChanges.trackTempControl),
      );
    }
    devices.forEach(({ location, specificLocation, machine }) =>
      dispatch(
        handleTrackTempControl({
          swt,
          location,
          specificLocation,
          machine,
          selection: Boolean(saveChanges.trackTempControl),
        }),
      ),
    );
  };

  // Resolves to the failure text, or null once the element is in the bank.
  const addElementToBank = async () => {
    try {
      await addAdminHeatersService(
        mapValues(saveInputElement, (value) => value.toString()),
      );
      dispatch(handleAddElementToBank(saveInputElement));
      return null;
    } catch (error) {
      logger.error(error);
      return `Failed to apply changes: ${error.message || 'Unknown error'}`;
    }
  };

  const saveEss = async () => {
    let message = 'no settings have been applied';
    if (tracking) {
      try {
        await applyTrackTempControl('ess', (deviceIds, body) =>
          updateSwitchSettingService(deviceIds, body, true),
        );
      } catch (error) {
        logger.error('track temperature control save failed', error);
        return saveFailedMessage(
          'administration settings',
          'ess-change options',
          error,
        );
      }
      message = TRACKED;
    }
    if (saveChanges.addElementToBank) {
      const failure = await addElementToBank();
      message = failure ?? ELEMENT_ADDED;
      setSaveChanges(noAdminChanges());
      if (!failure && saveChanges.trackTempControl) {
        message = BOTH_APPLIED;
      }
    }
    return adminMessage('ess', message);
  };

  const saveTgs = async () => {
    const gasDevices = saveChanges.selectGasType
      ? selectedSpecDevices(admin.tgsSpec, 'isSelectedGasType')
      : [];
    const valves = saveChanges.valveSettings
      ? confirmedValves(admin.tgsSpec, admin.tgs)
      : [];

    try {
      await Promise.all([
        gasDevices.length > 0 &&
          bulkUpdateBlowerDeviceService(
            machinesOf(gasDevices),
            'TGS',
            gasTypePayload(gasSelection),
          ),
        valves.length > 0 &&
          updateDevicesValveService(valveSettingsPayload(valves)),
      ]);
    } catch (error) {
      logger.error('gas settings save failed', error);
      return saveFailedMessage(
        'administration settings',
        'tgs-change options',
        error,
      );
    }

    let message = 'please confirm each gas value position before applying';
    if (saveChanges.selectGasType) {
      message = 'select gas type settings have been applied';
      gasDevices.forEach(({ location, specificLocation, machine }) =>
        dispatch(
          handleGasType({
            location,
            specificLocation,
            machine,
            value: gasSelection === 0 ? 'lp' : 'ng',
          }),
        ),
      );
    }
    if (saveChanges.valveSettings) {
      message = 'valve settings settings have been applied';
      valves.forEach(({ valveLocation, machine }) =>
        dispatch(
          handleGasValuePosition({
            location: valveLocation,
            machine,
            position: 'isApply',
            value: true,
          }),
        ),
      );
    }
    if (saveChanges.valveSettings && saveChanges.selectGasType) {
      message = 'valve settings & select gas type settings have been applied';
    }
    setSaveChanges(noAdminChanges());
    return adminMessage('tgs', message);
  };

  const saveTes = async () => {
    let title = 'settings';
    let message = 'no settings have been applied';
    if (tracking) {
      try {
        await applyTrackTempControl('tes', (deviceIds, body) =>
          updateBlowerSettingService(deviceIds, body, true),
        );
      } catch (error) {
        logger.error('track temperature control save failed', error);
        return saveFailedMessage(
          'administration settings',
          'tes-change options',
          error,
        );
      }
      title = 'change options';
      message = TRACKED;
    }
    if (saveChanges.addElementToBank) {
      title = 'change options';
      const failure = await addElementToBank();
      message = failure ?? ELEMENT_ADDED;
      if (!failure) {
        setSaveChanges(noAdminChanges());
      }
      if (!failure && saveChanges.trackTempControl) {
        message = BOTH_APPLIED;
      }
    }
    return adminMessage('tes', message, title);
  };

  const saveSystemCommands = async () => {
    let title = 'change options';
    let message = 'no settings have been applied';
    let failed = false;

    if (saveChanges.systemIdentification) {
      const sysIdOutcome = await saveSystemIdentification(
        saveChanges.systemIdentification,
      );
      if (sysIdOutcome) {
        ({ title, message, failed } = sysIdOutcome);
      }
    }
    if (saveChanges.forceGasAndElectric) {
      const devices = selectedSpecDevices(admin.sysSpec, 'isSelected');
      try {
        if (devices.length > 0) {
          await updateBlowerSettingService(
            machinesOf(devices),
            forceGasAndElectricPayload(enableSwitch),
            true,
          );
        }
      } catch (error) {
        logger.error('force gas & electric save failed', error);
        // A system identification saved above must not be sent again on retry.
        setSaveChanges((changes) => ({
          ...changes,
          systemIdentification: failed ? changes.systemIdentification : false,
        }));
        return {
          content: saveFailedMessage('', '', error),
          keepOpen: true,
        };
      }
      title = 'change options';
      message = FORCED;
      devices.forEach(({ location, specificLocation, machine }) =>
        dispatch(
          handleForceGasAndElectric({
            location,
            specificLocation,
            machine,
            selection: enableSwitch ? 'enable' : 'disable',
          }),
        ),
      );
    }
    if (
      !failed &&
      saveChanges.forceGasAndElectric &&
      saveChanges.systemIdentification
    ) {
      message = IDENTIFIED_AND_FORCED;
    }
    if (!failed) {
      setSaveChanges(noAdminChanges());
    }
    return { content: plainMessage(title, message), keepOpen: failed };
  };

  const save = async (header) => {
    switch (header) {
      case SETTINGS_HEADERS.ESS:
        return { content: await saveEss() };
      case SETTINGS_HEADERS.TGS:
        return { content: await saveTgs() };
      case SETTINGS_HEADERS.TES:
        return { content: await saveTes() };
      case SETTINGS_HEADERS.SYSTEM_COMMANDS:
        return saveSystemCommands();
      default:
        setSaveChanges(noAdminChanges());
        return { content: settingsMessage('', '', '', '') };
    }
  };

  return {
    panelProps: {
      saveChanges,
      setSaveChanges,
      setSaveInputElement,
      gasSelection,
      setGasSelection,
      enableSwitch,
      setEnableSwitch,
      selectSystemConfig,
      setSelectSystemConfig,
    },
    save,
  };
};
